import React, { useRef, useState } from "react";
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton, IonGrid, IonRow, IonCol, IonImg, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const seasons = [
    { name: "Circle", images: ["circle.png", "sun.png", "moon.png", "clock.png", "wheel.png", "ball.png"] },
    { name: "Square", images: ["square.png", "chessboard.png", "breadslice.png", "window.png", "photoframe.png", "wallclocksquare.png"] },
    { name: "Triangle", images: ["triangle.png", "pizzaslice.png", "trafficsign.png", "walermelonpiece.png", "sandwitchpiece.png", "christmastree.png"] },
    { name: "Star", images: ["star.png", "starfish.png", "starpendent.png", "starballoon.png", "starcookies.png", "starglasses.png"] },
    { name: "Diamond", images: ["diamond.png", "kite.png", "playingcard.png", "diamondbiscuit.png", "diamondbutton.png", "diamondearing.png"] },
    { name: "Rectangle", images: ["mobilephone.png", "door.png", "rectangleframe.png", "television.png", "rectangleblackboard.png"] },
    { name: "Pentagon", images: ["house.png", "kennel.png", "pentagontrafficsign.png", "pentagonbutton.png", "pentagonkeyring.png"] },
];

const getRandomUniqueOptions = (correctOption: { name: any; images?: string[]; }) => {
    const options = [correctOption];
    const availableOptions = seasons.filter(season => season.name !== correctOption.name);
    while (options.length < 4) {
        const randomIndex = Math.floor(Math.random() * availableOptions.length);
        options.push(availableOptions[randomIndex]);
        availableOptions.splice(randomIndex, 1);
    }
    return options.sort(() => Math.random() - 0.5);
};

const IndentifyShapes = () => {
    const getNewQuestion = () => {
        document.title = "Identify Basic Shapes Worksheet for LKG | Circle, Square, Triangle | Mama’s Guidance"; // Set page title
        const newQuestion = seasons[Math.floor(Math.random() * seasons.length)];
        const randomImage = newQuestion.images[Math.floor(Math.random() * newQuestion.images.length)];
        return { newQuestion, newImage: randomImage, newOptions: getRandomUniqueOptions(newQuestion) };
    };

    const [{ newQuestion: question, newImage: image, newOptions: options }, setQuizState] = useState(getNewQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const handleAnswer = (selected: string) => {
        if (selected === question.name) {
            setShowImage(true);
            setTimeout(() => {
                setQuizState(getNewQuestion());
                setFeedback("");
                setShowImage(false);
            }, 5000)
        } else {
            setFeedback("❌ Try Again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help LKG kids recognize and name basic shapes with fun, visual activities on Mama’s Guidance—designed to build early geometry skills through interactive shape identification and matching games.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord matchImage match-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Identify Shapes</h1>

                                    <p>
                                        Recognizing and naming basic shapes is an important early math skill for preschoolers. This worksheet helps LKG children identify common 2D shapes like <strong>circle</strong>, <strong>square</strong>, <strong>triangle</strong>, <strong>rectangle</strong>, and more through colorful and engaging activities.
                                    </p>

                                    <h2>🔷 What Are Shapes?</h2>
                                    <p>
                                        Shapes are everywhere around us! From a <strong>circle</strong> in a clock to a <strong>square</strong> window, children love spotting and naming them. Learning to identify shapes enhances visual perception, spatial understanding, and early geometry skills.
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify and name basic <strong>2D shapes</strong></li>
                                        <li>Match shapes with everyday objects</li>
                                        <li>Distinguish between similar shapes</li>
                                        <li>Improve fine motor skills through <strong>coloring and tracing shapes</strong></li>
                                    </ul>

                                    <h2>📋 What’s Inside the Worksheet?</h2>
                                    <ul>
                                        <li>Recognize and name shapes like <strong>circle, square, triangle, rectangle, and oval</strong></li>
                                        <li>Match shapes to real-world objects (<strong>e.g., ball = circle, book = rectangle</strong>)</li>
                                        <li>Color specific shapes as instructed</li>
                                        <li>Shape sorting and grouping tasks</li>
                                    </ul>

                                    <h2>👩‍🏫 Teaching Tips for Parents</h2>
                                    <ul>
                                        <li>Use household items like coins, boxes, plates, and toys to teach shape identification</li>
                                        <li>Play shape hunt games: <strong>“Can you find something that’s a triangle?”</strong></li>
                                        <li>Draw shapes on paper and ask your child to name or color them</li>
                                        <li>Sing shape songs or watch animated videos to reinforce learning</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/missing-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Missing Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/skip-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Skip Number by 2 or 5</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Go for a <strong>“Shape Walk”</strong> around your home or park. Ask your child to spot shapes in nature and objects — learning through play is the best way!
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Identify Shapes</h1>

                                <p>
                                    Recognizing and naming basic shapes is an important early math skill for preschoolers. This worksheet helps LKG children identify common 2D shapes like <strong>circle</strong>, <strong>square</strong>, <strong>triangle</strong>, <strong>rectangle</strong>, and more through colorful and engaging activities.
                                </p>

                                <h2>🔷 What Are Shapes?</h2>
                                <p>
                                    Shapes are everywhere around us! From a <strong>circle</strong> in a clock to a <strong>square</strong> window, children love spotting and naming them. Learning to identify shapes enhances visual perception, spatial understanding, and early geometry skills.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify and name basic <strong>2D shapes</strong></li>
                                    <li>Match shapes with everyday objects</li>
                                    <li>Distinguish between similar shapes</li>
                                    <li>Improve fine motor skills through <strong>coloring and tracing shapes</strong></li>
                                </ul>

                                <h2>📋 What’s Inside the Worksheet?</h2>
                                <ul>
                                    <li>Recognize and name shapes like <strong>circle, square, triangle, rectangle, and oval</strong></li>
                                    <li>Match shapes to real-world objects (<strong>e.g., ball = circle, book = rectangle</strong>)</li>
                                    <li>Color specific shapes as instructed</li>
                                    <li>Shape sorting and grouping tasks</li>
                                </ul>

                                <h2>👩‍🏫 Teaching Tips for Parents</h2>
                                <ul>
                                    <li>Use household items like coins, boxes, plates, and toys to teach shape identification</li>
                                    <li>Play shape hunt games: <strong>“Can you find something that’s a triangle?”</strong></li>
                                    <li>Draw shapes on paper and ask your child to name or color them</li>
                                    <li>Sing shape songs or watch animated videos to reinforce learning</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/missing-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Missing Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/skip-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Skip Number by 2 or 5</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Go for a <strong>“Shape Walk”</strong> around your home or park. Ask your child to spot shapes in nature and objects — learning through play is the best way!
                                </p>
                            </article>
                            <img src={process.env.REACT_APP_API_URL + image} className="img-thumbnail season-image helper-image" />
                            <div className="mt-4" style={{ display: 'ruby' }}>
                                {options.map((option, index) => (
                                    <div key={index} style={{ margin: '10px' }}>
                                        <button className="optionButton background-blue" onClick={() => handleAnswer(option.name)}>{option.name}</button>
                                    </div>
                                ))}
                            </div>
                            <h3>{feedback}</h3>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_LOADING_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default IndentifyShapes;
